/**
 * The walkthroughs, declared (INCO-387).
 *
 * `OnboardingChecklist` told a new desk the five things to do. It did not tell
 * them how any of them work, and for somebody whose previous system was a
 * WhatsApp group and a spreadsheet, "Publish your first brief" is a destination
 * without a route.
 *
 * **Anchors are contract, not CSS.** A step targets `data-tour="brief-publish"`,
 * never a class or an nth-child. `spec/design/tours_spec.rb` fails when a
 * declared anchor exists nowhere in the frontend, because the alternative — a
 * refactor silently removing the element and the tooltip pointing at nothing —
 * is what makes tours feel broken and gets them switched off for good.
 *
 * **They interrupt an empty screen only.** Auto-start fires once, on first
 * visit, and only when the surface has nothing on it: the same condition that
 * renders the empty state. Nobody working a full pipeline gets a spotlight
 * thrown over it. Every tour stays re-runnable from the help centre, because
 * the day you want the deals walkthrough is the day you get your first deal,
 * which is not the day you first opened the deals page.
 */
export interface TourStep {
  /** The `data-tour` value this step points at. */
  anchor: string
  title: string
  body: string
  /** Optional: the help article to read afterwards. */
  help?: string
}

export interface Tour {
  key: string
  title: string
  /** What the reader gets out of it, shown in the help centre listing. */
  summary: string
  /**
   * The route helper this tour belongs to. Auto-start only considers the tour
   * whose surface you are on, and the help centre groups them by it.
   */
  surface: string
  steps: TourStep[]
}

export const TOURS: Tour[] = [
  {
    key: "desk-overview",
    title: "Around your desk",
    summary: "What each part of the sidebar is for, and where the day starts.",
    surface: "account_root",
    steps: [
      {
        anchor: "nav-today",
        title: "Today",
        body: "A bird's eye view of what needs you today, including unread offers, quiet follow-ups and anything overdue.",
      },
      {
        anchor: "nav-inbox",
        title: "Inbox",
        body: "Everything that came in waits here until you decide: offers from WhatsApp, the portal or a paste, and briefs other desks sent you. Nothing joins your book without you.",
        help: "your-inbox",
      },
      {
        anchor: "nav-opportunities",
        title: "Opportunities",
        body: "One record per cargo, holding the specification, the counterparties, the briefs you send and everything the desk has said about it.",
        help: "turn-an-offer-into-an-opportunity",
      },
      {
        anchor: "nav-mandates",
        title: "The network",
        body: "Everything about dealing with other desks: mandates you can prove, the graph, confirmed trades. The desks you are connected to are in Settings.",
        help: "connect-with-another-desk",
      },
      {
        anchor: "help-trigger",
        title: "Help and the assistant",
        body: "The question mark explains the screen you are on without leaving it, and the assistant beside it answers questions.",
      },
    ],
  },
  {
    key: "offer-intake",
    title: "From a forwarded message to a record",
    summary: "Capture an offer, read what came out of it, and confirm it into your book.",
    surface: "inbox",
    steps: [
      {
        anchor: "intake-capture",
        title: "Paste the whole thing",
        body: "Greeting, signature, six lines of disclaimer and all. Inco reads past them and pulls out the commodity, quantity, price and terms.",
        help: "capture-your-first-offer",
      },
      {
        anchor: "intake-queue",
        title: "Nothing is saved yet",
        body: "Captured offers wait here. Most of what arrives in a group is not worth a record, and the inbox is where you decide which is which.",
        help: "your-inbox",
      },
    ],
  },
  {
    key: "brief-and-send",
    title: "Write a brief and send it",
    summary: "Turn a cargo into a page with your name on it, then send it to named counterparties.",
    surface: "opportunities",
    steps: [
      {
        anchor: "opportunity-list",
        title: "Open the cargo",
        body: "A brief is written on an opportunity. Open one, and the Brief tab is where the offer becomes something a counterparty can read.",
        help: "write-and-publish-a-brief",
      },
      {
        anchor: "help-trigger",
        title: "Then send it to people",
        body: "A published brief goes to counterparties you choose, each on their own link, so you know exactly who opened it.",
        help: "send-a-brief-to-counterparties",
      },
    ],
  },
  {
    key: "deals-and-earnings",
    title: "What the desk earns",
    summary: "Log a deal, set the commission, and close it out properly.",
    surface: "deals",
    steps: [
      {
        anchor: "deals-list",
        title: "One attempt, one deal",
        body: "Three buyers working the same parcel are three deals. Each has its own stage, its own paperwork and its own money.",
        help: "log-a-deal-and-what-you-earn",
      },
      {
        anchor: "deals-list",
        title: "Gross is not your money",
        body: "500 MT at $21,400 is a $10.7m trade and a $267,500 fee. Inco shows both and never lets the first stand in for the second.",
        help: "log-a-deal-and-what-you-earn",
      },
      {
        anchor: "deals-list",
        title: "Close it out",
        body: "When it trades, record what actually happened. Those figures are what your reporting and your track record are built from.",
        help: "move-a-deal-through-the-pipeline",
      },
    ],
  },
  {
    key: "the-network",
    title: "Dealing with other desks",
    summary: "Mandates you can prove, and what a confirmed trade is worth.",
    surface: "mandates",
    steps: [
      {
        anchor: "nav-mandates",
        title: "Prove your mandate",
        body: "Everybody says direct mandate. A confirmed one is the principal saying so, on a link they answered — and a badge you cannot award yourself.",
        help: "prove-your-mandate",
      },
    ],
  },
]

export function tourFor(surface: string | null | undefined): Tour | undefined {
  if (!surface) return undefined

  return TOURS.find((tour) => tour.surface === surface)
}

export function tourByKey(key: string): Tour | undefined {
  return TOURS.find((tour) => tour.key === key)
}
